import { z } from 'zod';
import { TIPOS_ORGANISMO, TIPOS_PROCESO } from '../dominio/enums.js';
import { diasSchema, enumDe, idSchema, montoSchema, porcentajeSchema } from './comunes.js';
/**
 * Petición de predicción.
 *
 * La hace el formulario de oferta mientras se escribe, igual que el
 * `triggerPredictor` del CRM original, pero ahora contra el servidor —que ve
 * todo el histórico— en vez de contra un array en memoria del navegador.
 */
export const solicitarPrediccionSchema = z
    .object({
    categoriaId: idSchema,
    presupuestoOficialNeto: montoSchema.optional().default(0),
    presupuestoOficialBruto: montoSchema.optional().nullable(),
    modalidadMonto: z.enum(['NETO', 'BRUTO']).optional().nullable(),
    organismoId: idSchema.optional().nullable(),
    tipoOrganismo: enumDe(TIPOS_ORGANISMO).optional().nullable(),
    tipoProceso: enumDe(TIPOS_PROCESO).optional().nullable(),
    plazoMaximoBasesDias: diasSchema.optional().nullable(),
    cantidadOferentesEsperada: z.number().int().min(0).max(500).optional().nullable(),
    /** Si se omite, se usa el margen objetivo de la categoría. */
    margenObjetivoPct: porcentajeSchema.optional().nullable(),
})
    .refine((valor) => (valor.presupuestoOficialNeto && valor.presupuestoOficialNeto > 0) ||
    (valor.presupuestoOficialBruto && valor.presupuestoOficialBruto > 0), 'Ingresa el presupuesto oficial para poder estimar');
/**
 * Parámetros del motor, editables por el dueño.
 *
 * En el CRM original estos números estaban escritos dentro del código
 * (la vida media de 8 meses, el factor 0.99 del precio, el 0.95 del plazo).
 * Exponerlos permite calibrar el motor con el backtesting en vez de a ciegas.
 */
export const actualizarParametrosPredictorSchema = z.object({
    vidaMediaMeses: z.number().min(1).max(60).optional(),
    agresividadPrecio: z.number().min(0.5).max(1.2).optional(),
    agresividadPlazo: z.number().min(0.5).max(1.2).optional(),
    pesoMismoOrganismo: z.number().min(1).max(10).optional(),
    pesoMismoTipoOrganismo: z.number().min(1).max(10).optional(),
    pesoMismoTipoProceso: z.number().min(1).max(10).optional(),
    winsorPct: z.number().min(0).max(25).optional(),
    margenMinimoPct: porcentajeSchema.optional(),
    plazoMinimoDias: z.number().int().min(1).max(90).optional(),
});
/** Petición de backtesting. Sin parámetros, evalúa la configuración vigente. */
export const solicitarBacktestSchema = z.object({
    categoriaId: idSchema.optional(),
    minimoObservaciones: z.number().int().min(1).max(20).default(3),
    /** Configuración alternativa a comparar con la vigente. */
    configuracionAlternativa: actualizarParametrosPredictorSchema.optional(),
});
/** Preset de estrategia en 1 clic */
export const aplicarEstrategiaSchema = z.object({
    modo: z.enum(['AGRESIVO', 'EQUILIBRADO', 'CONSERVADOR']),
});
/** Parámetros para el simulador / playground interactivo */
export const simuladorPlaygroundSchema = z.object({
    presupuestoOficialNeto: montoSchema.refine((valor) => valor > 0, 'Ingresa el presupuesto oficial para poder estimar'),
    sector: z.string().optional().nullable(),
    organismoId: idSchema.optional().nullable(),
    categoriaId: idSchema.optional().nullable(),
    cantidadOferentes: z.number().int().min(1).max(500).default(3),
    costoDirectoNeto: montoSchema.optional().nullable(),
    margenMinimoPct: porcentajeSchema.optional().nullable(),
    tipoOrganismo: enumDe(TIPOS_ORGANISMO).optional().nullable(),
    tipoProceso: enumDe(TIPOS_PROCESO).optional().nullable(),
});
/** Petición de test de impacto antes vs después */
export const testImpactoSchema = z.object({
    modo: z.enum(['AGRESIVO', 'EQUILIBRADO', 'CONSERVADOR']).optional().nullable(),
    parametros: actualizarParametrosPredictorSchema.optional().nullable(),
    categoriaId: idSchema.optional().nullable(),
});
//# sourceMappingURL=prediccion.js.map